'use client';

import React, { type ReactNode, type RefObject } from 'react';
import { cn } from '../../utils/cn';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import type { AvailableUpgradeTargetItem } from '../../services/api';
import { UpgradeInventoryList } from './UpgradeInventoryList';
import { UpgradeTargetList } from './UpgradeTargetList';
import { useUpgradeTargetFilters } from './useUpgradeTargetFilters';
import translations from './UpgradeScreen.translations';
import type { UpgradeItem } from './UpgradeScreen';

const PANEL_BACKGROUND = 'linear-gradient(180deg, rgba(15,18,30,0.95) 0%, rgba(10,12,20,0.98) 100%)';
const PANEL_BODY_CLASS = 'px-3 pb-3';

const Panel: React.FC<{ title: string; headerEnd?: ReactNode; disabled?: boolean; children: ReactNode }> = ({
  title,
  headerEnd = null,
  disabled = false,
  children,
}) => (
  <section
    className={cn('min-w-0 rounded-2xl border border-white/[0.06]', disabled && 'pointer-events-none opacity-50')}
    style={{ background: PANEL_BACKGROUND }}
  >
    <div className="flex min-h-[48px] items-center justify-between gap-3 px-4 pt-3 pb-2">
      <h2 className="m-0 truncate text-sm font-bold text-white">{title}</h2>
      {headerEnd}
    </div>
    {children}
  </section>
);

interface InventoryPanelProps {
  onSelectItem: (item: UpgradeItem) => void;
  scrollRef: RefObject<HTMLDivElement | null>;
  selectedId: string | null;
  disabled?: boolean;
}

export const UpgradeDesktopInventoryPanel: React.FC<InventoryPanelProps> = ({ onSelectItem, scrollRef, selectedId, disabled = false }) => {
  const { t } = useLocalTranslation(translations);

  return (
    <Panel title={t('slotRoleSource')} disabled={disabled}>
      <div className={PANEL_BODY_CLASS}>
        <UpgradeInventoryList onSelectItem={onSelectItem} scrollRef={scrollRef} selectedId={selectedId} paged />
      </div>
    </Panel>
  );
};

interface TargetsPanelProps {
  sourceItemId: string | null;
  sourceAmount?: number | null;
  selectedHashName: string | null;
  onSelectItem: (item: AvailableUpgradeTargetItem) => void;
  scrollRef: RefObject<HTMLDivElement | null>;
  disabled?: boolean;
}

export const UpgradeDesktopTargetsPanel: React.FC<TargetsPanelProps> = ({
  sourceItemId,
  sourceAmount = null,
  selectedHashName,
  onSelectItem,
  scrollRef,
  disabled = false,
}) => {
  const { t } = useLocalTranslation(translations);
  const { headerNode, filtersNode, queryFilters } = useUpgradeTargetFilters();

  return (
    <Panel title={t('slotRoleTarget')} headerEnd={sourceItemId ? headerNode : null} disabled={disabled}>
      {sourceItemId ? (
        <>
          {filtersNode}
          <div className={PANEL_BODY_CLASS}>
            <UpgradeTargetList
              sourceItemId={sourceItemId}
              sourceAmount={sourceAmount}
              queryFilters={queryFilters}
              onSelectItem={onSelectItem}
              scrollRef={scrollRef}
              selectedHashName={selectedHashName}
              paged
            />
          </div>
        </>
      ) : (
        <div className="grid min-h-[280px] place-items-center px-6 pb-4 text-center text-sm text-label-secondary">
          {t('selectSourceFirst')}
        </div>
      )}
    </Panel>
  );
};
